<script setup lang="ts">
import { ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import {
  CATEGORY_LABELS,
  acceptAll,
  consent,
  consentPanelOpen,
  consentPending,
  refuseAll,
  saveConsent,
  type ConsentCategory,
} from '@/lib/consent';

/**
 * Bandeau de consentement. « Tout refuser » a le même poids visuel que « Tout
 * accepter » (exigence CNIL) ; fermer sans choisir n'accepte rien.
 */
const categories = Object.keys(CATEGORY_LABELS) as ConsentCategory[];
const draft = ref({ ...consent.value });
const customizing = ref(false);

watch(consentPanelOpen, (open) => {
  if (open) {
    draft.value = { ...consent.value };
    customizing.value = true;
  }
});
</script>

<template>
  <div
    v-if="consentPending || consentPanelOpen"
    role="dialog"
    data-consent-banner
    aria-labelledby="consent-title"
    class="fixed inset-x-0 bottom-0 z-50 p-4 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md sm:p-0"
  >
    <div class="rounded-lg border border-slate-200 bg-surface p-5 text-sm text-ink-soft shadow-xl">
      <div class="flex items-start justify-between gap-3">
        <h2 id="consent-title" class="font-semibold text-ink">Cookies et traceurs</h2>
        <button type="button" class="text-xs underline" aria-label="Continuer sans accepter" @click="refuseAll">
          Continuer sans accepter
        </button>
      </div>
      <p class="mt-2">
        Les cookies nécessaires (session, préférences) sont toujours actifs. Avec votre accord, nous mesurons aussi
        l'audience des pages publiques (Google Analytics). Rien n'est déposé sans votre choix, modifiable à tout moment.
        <RouterLink to="/confidentialite" class="underline">En savoir plus</RouterLink>
      </p>

      <fieldset v-if="customizing" class="mt-4 space-y-3">
        <legend class="sr-only">Choix par finalité</legend>
        <label class="flex items-start gap-3">
          <input type="checkbox" checked disabled class="mt-0.5" />
          <span><strong class="text-ink">Strictement nécessaires</strong><br />Connexion, sécurité, thème. Toujours actifs.</span>
        </label>
        <label v-for="c in categories" :key="c" class="flex items-start gap-3">
          <input v-model="draft[c]" type="checkbox" class="mt-0.5" />
          <span><strong class="text-ink">{{ CATEGORY_LABELS[c].title }}</strong><br />{{ CATEGORY_LABELS[c].description }}</span>
        </label>
      </fieldset>

      <div class="mt-4 flex flex-wrap gap-2">
        <button type="button" class="btn-ghost flex-1" @click="refuseAll">Tout refuser</button>
        <button type="button" class="btn-ghost flex-1" @click="acceptAll">Tout accepter</button>
        <button v-if="customizing" type="button" class="btn-primary w-full" @click="saveConsent(draft)">
          Enregistrer mes choix
        </button>
        <button v-else type="button" class="w-full text-xs underline" @click="customizing = true">Personnaliser</button>
      </div>
    </div>
  </div>
</template>
